import { get, isEqual } from 'lodash'
import type {
  DigestedSchema,
  EditorSDK,
} from '@wix/wix-data-client-common-standalone'
import { REPEATER_ROLE } from '@wix/wix-data-client-common-standalone'
import { shouldConnectionChangeAffectRepeater } from '../../business-logic/datasets/shouldConnectionChangeAffectRepeater'
import type { BindingConfig, DatasetType } from '@wix/wix-data-client-common'
import {
  DATASET,
  schemaFetcherV2,
  WIDGET_DATASET,
  ROUTER_DATASET,
} from '@wix/wix-data-client-common'
import { getRouterDatasetConfig } from '../../business-logic/datasets/routerDataset'
import type { ComponentRef } from '@wix/editor-platform-sdk-types'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import { match } from 'ts-pattern'

export const checkIfComponentHasNoOtherPrimaryConnection = async ({
  editorSdkProxy,
  componentRef,
  role,
}: {
  editorSdkProxy: EditorSDK
  componentRef: ComponentRef
  role: string
}) => {
  const componentConnections = await editorSdkProxy.controllers.listConnections(
    { componentRef },
  )
  const componentHasNoOtherPrimaryConnection = componentConnections.every(
    componentConnection =>
      !componentConnection.isPrimary || componentConnection.role === role,
  )
  return componentHasNoOtherPrimaryConnection
}

export type RepeaterAncestor = null | {
  componentRef: ComponentRef
  controllerRef: null | ComponentRef
}

export const connectRepeaterAncestorIfNeeded = async ({
  editorSdkProxy,
  componentRef,
  repeaterAncestor,
  controllerRef,
}: {
  editorSdkProxy: EditorSDK
  componentRef: ComponentRef
  repeaterAncestor: RepeaterAncestor
  controllerRef: ComponentRef
}): Promise<undefined | { controllerRef: ComponentRef }> => {
  if (repeaterAncestor) {
    const shouldUpdateRepeaterConnection =
      await shouldConnectionChangeAffectRepeater({
        editorSdkProxy,
        componentRef,
        repeaterAncestor,
        connecting: true,
      })

    if (shouldUpdateRepeaterConnection) {
      const isPrimary = await checkIfComponentHasNoOtherPrimaryConnection({
        editorSdkProxy,
        componentRef: repeaterAncestor.componentRef,
        role: REPEATER_ROLE,
      })

      await editorSdkProxy.controllers.connect({
        controllerRef,
        role: REPEATER_ROLE,
        connectionConfig: {},
        connectToRef: repeaterAncestor.componentRef,
        isPrimary,
      })
      return { controllerRef }
    }
  }
  return undefined
}

export const clearAllControllerConnections = async ({
  editorSdkProxy,
  controllerRef,
}: {
  editorSdkProxy: EditorSDK
  controllerRef: ComponentRef
}) => {
  const connectedComponentRefs =
    await editorSdkProxy.controllers.listConnectedComponents({
      controllerRef,
    })
  await Promise.all(
    connectedComponentRefs.map(componentRef =>
      editorSdkProxy.controllers
        .listConnections({ componentRef })
        .then(componentConnections =>
          Promise.all(
            componentConnections
              .filter(connection =>
                isEqual(controllerRef, connection.controllerRef),
              )
              .map(connectionToOurController =>
                editorSdkProxy.controllers.disconnect({
                  controllerRef,
                  connectToRef: componentRef,
                  role: connectionToOurController.role,
                }),
              ),
          ),
        ),
    ),
  )
}

export const fetchCollectionSchemas = async (
  collectionsApi: CollectionsApi,
  { includeReferenceData = true, includeHiddenAppCollections = true } = {},
) =>
  await schemaFetcherV2.fetchAll(collectionsApi, {
    includeReferenceData,
    includeHiddenAppCollections,
  })

export const getConnectedField = async ({
  editorSdkProxy,
  controllerRef,
  collections,
  newBindings,
}: {
  editorSdkProxy: EditorSDK
  controllerRef: ComponentRef
  collections: DigestedSchema[]
  newBindings: BindingConfig[]
}) => {
  const { type, config: datasetConfig } =
    await editorSdkProxy.controllers.getData({
      controllerRef,
    })

  const config = await match(type as DatasetType)
    .with(ROUTER_DATASET, () =>
      getRouterDatasetConfig(editorSdkProxy, controllerRef, datasetConfig),
    )
    .with(DATASET, WIDGET_DATASET, () => datasetConfig)
    .exhaustive()

  const collectionName = get(config, ['dataset', 'collectionName'])
  const collection = collections.find(({ id }) => id === collectionName)

  const field = collection?.fields.find(
    // @ts-expect-error typing
    ({ name }) => name === newBindings[0]?.fieldPath,
  )

  return field
}
